/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Select } from '@base-ui/react/select';
import cx from 'classnames';
import { Icon } from 'sarsaparilla';

import { ACTIVITY_TAG_OPTIONS } from '../constants/activityTags';
import type { ActivityTagOption } from '../constants/activityTags';

import styles from './QuickFilterActivityTags.module.scss';

interface QuickFilterSelectProps {
    selectedValues: string[];
    onChange: (values: string[]) => void;
    options: { label: string; value: string }[];
}

function getDisplayText(
    selectedValues: string[],
    options: { label: string; value: string }[]
): React.ReactNode {
    if (selectedValues.length === 0) {
        return 'Nearby Activities ...';
    }

    const firstOption = options.find((o) => o.value === selectedValues[0]);
    const firstLabel = firstOption?.label ?? selectedValues[0];

    if (selectedValues.length === 1) {
        return firstLabel;
    }

    return (
        <span>
            {firstLabel}
            <span className={styles.plusMore}>
                {` (+${selectedValues.length - 1} more)`}
            </span>
        </span>
    );
}

export function QuickFilterSelect({
    selectedValues,
    onChange,
    options,
}: QuickFilterSelectProps) {
    return (
        <Select.Root
            multiple
            items={options}
            value={selectedValues}
            onValueChange={(newValues) => {
                onChange(newValues);
            }}
        >
            <Select.Trigger className={styles.trigger} aria-label="Nearby Activities">
                <Select.Value
                    className={cx(styles.triggerText, {
                        [styles.placeholder]: selectedValues.length === 0,
                    })}
                    placeholder="Nearby Activities ..."
                >
                    {getDisplayText(selectedValues, options)}
                </Select.Value>
                <Select.Icon className={styles.triggerIcon}>
                    <Icon iconName="chevron-down" />
                </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
                <Select.Positioner
                    className={styles.positioner}
                    sideOffset={4}
                    side="bottom"
                    align="start"
                    alignItemWithTrigger={false}
                >
                    <Select.Popup className={styles.popup}>
                        <div className={styles.popupContent}>
                            <div className={styles.popupHeader}>
                                Filter by nearby activities ...
                            </div>
                            <div className={styles.popupScrollArea}>
                                <Select.ScrollUpArrow className={styles.scrollArrow}>
                                    <Icon iconName="chevron-up" />
                                </Select.ScrollUpArrow>
                                <Select.List className={styles.list}>
                                    {options.map((option) => {
                                        const isSelected = selectedValues.includes(
                                            option.value
                                        );
                                        return (
                                            <Select.Item
                                                key={option.value}
                                                value={option.value}
                                                className={styles.item}
                                                data-ga-tracking-id="481629307542"
                                                data-ga-tracking-value={`${option.label} - ${
                                                    isSelected ? 'Disabled' : 'Enabled'
                                                }`}
                                            >
                                                <Select.ItemIndicator
                                                    className={styles.itemIndicator}
                                                >
                                                    <Icon iconName="check" />
                                                </Select.ItemIndicator>
                                                <Select.ItemText
                                                    className={styles.itemText}
                                                >
                                                    {option.label}
                                                </Select.ItemText>
                                            </Select.Item>
                                        );
                                    })}
                                </Select.List>
                            </div>
                            <Select.ScrollDownArrow className={styles.scrollArrow}>
                                <Icon iconName="chevron-down" />
                            </Select.ScrollDownArrow>
                        </div>
                    </Select.Popup>
                </Select.Positioner>
            </Select.Portal>
        </Select.Root>
    );
}

function toFilterItem(option: ActivityTagOption) {
    return {
        key: option.value,
        value: option.label,
        filters: [
            {
                attribute: 'activity_tags',
                filterValue: option.value,
            },
        ],
    };
}

interface QuickFilterActivityTagsProps {
    uiFilters: {
        activityTags: Record<
            string,
            {
                key: string;
                value: string;
                filters: { attribute: string; filterValue: string }[];
            }
        >;
    };
    onFilterToggle: (filterName: string, item: ReturnType<typeof toFilterItem>) => void;
    onApplyFilters: () => void;
}

export function QuickFilterActivityTags({
    uiFilters,
    onFilterToggle,
    onApplyFilters,
}: QuickFilterActivityTagsProps) {
    const selections = uiFilters.activityTags || {};
    const selectedValues = Object.keys(selections);

    const handleChange = (newValues: string[]) => {
        const added = newValues.filter((v) => !selectedValues.includes(v));
        const removed = selectedValues.filter((v) => !newValues.includes(v));

        const changed = [...added, ...removed];
        changed.forEach((tagValue) => {
            const option = ACTIVITY_TAG_OPTIONS.find((o) => o.value === tagValue);
            if (option) {
                onFilterToggle('activityTags', toFilterItem(option));
            }
        });

        onApplyFilters();
    };

    return (
        <div className={styles.quickFilterWrap}>
            <QuickFilterSelect
                selectedValues={selectedValues}
                onChange={handleChange}
                options={ACTIVITY_TAG_OPTIONS}
            />
        </div>
    );
}
